import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Function to get proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9Ijc1IiB5PSI3NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiBmaWxsPSIjOWE5YmFiIj5ObyBJbWFnZTwvdGVh0Pjwvc3ZnPg==";
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http')) return imagePath;
  
  // If it's a relative path, construct full URL
  if (imagePath.startsWith('/')) {
    return `${window.location.origin}${imagePath}`;
  }
  
  // For other cases, try to construct URL
  const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
  
  // Handle different environments
  let baseUrl;
  const { hostname, port, protocol } = window.location;
  
  if (hostname === 'localhost' && port === '3000') {
    // React dev server on port 3000, APIs on port 80
    baseUrl = `${protocol}//${hostname}`;
  } else if (hostname === 'localhost' && !port) {
    // React build served from same domain as APIs
    baseUrl = window.location.origin;
  } else {
    // Production environment
    baseUrl = window.location.origin;
  }
  
  return `${baseUrl}/apis/${cleanPath}`;
};

// Handle image loading errors
const handleImageError = (e) => {
  console.warn("Image failed to load:", e.target.src);
  e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9Ijc1IiB5PSI3NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiBmaWxsPSIjOWE5YmFiIj5ObyBJbWFnZTwvdGVh0Pjwvc3ZnPg==";
  e.target.alt = "Image not available";
  e.target.className = e.target.className + " bg-gray-200";
};

const CartItem = ({ item, onQuantityChange, onRemove }) => (
  <div className="justify-between mb-6 rounded-lg bg-white p-6 shadow-md sm:flex sm:justify-start">
    <div className="flex-shrink-0">
      <img
        src={getImageUrl(item.product_image)}
        alt={item.product_name || "Product image"}
        className="w-[120px] h-[120px] rounded-lg sm:w-40 object-cover"
        onError={handleImageError}
        loading="lazy"
      />
    </div>
    <div className="sm:ml-4 sm:flex sm:w-full sm:justify-between">
      <div className="mt-5 sm:mt-0">
        <h2 className="text-lg font-bold text-gray-900">{item.product_name}</h2>
        <p className="mt-1 text-xs text-gray-700">{item.variant}</p>
        <p className="mt-1 text-xs text-gray-700">Price: ₹{item.price}</p>
      </div>
      <div className="mt-4 flex justify-between sm:space-y-6 sm:mt-0 sm:block sm:space-x-6">
        <div className="flex items-center border-gray-100">
          <span
            className="cursor-pointer rounded-l bg-gray-100 py-1 px-3.5 duration-100 hover:bg-blue-500 hover:text-blue-50"
            onClick={() => onQuantityChange(item.product_id, item.quantity - 1)}
          >
            -
          </span>
          <input
            className="h-8 w-8 border bg-white text-center text-xs outline-none"
            type="number"
            value={item.quantity}
            min="1"
            onChange={(e) =>
              onQuantityChange(item.product_id, parseInt(e.target.value, 10))
            }
          />
          <span
            className="cursor-pointer rounded-r bg-gray-100 py-1 px-3 duration-100 hover:bg-blue-500 hover:text-blue-50"
            onClick={() => onQuantityChange(item.product_id, item.quantity + 1)}
          >
            +
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <p className="text-sm">₹{(item.price * item.quantity).toFixed(2)}</p>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="h-5 w-5 cursor-pointer duration-150 hover:text-red-500"
            onClick={() => onRemove(item.product_id)}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
      </div>
    </div>
  </div>
);

const Cart = () => {
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(savedCart);
  }, []);

  const handleRemoveFromCart = useCallback(
    (productId) => {
      const updatedCart = cart.filter((item) => item.product_id !== productId);
      setCart(updatedCart);
      localStorage.setItem("cart", JSON.stringify(updatedCart));
    },
    [cart]
  );

  const handleQuantityChange = useCallback(
    (productId, newQuantity) => {
      if (newQuantity < 1) {
        alert("Quantity must be at least 1.");
        return;
      }

      const updatedCart = cart.map((item) =>
        item.product_id === productId
          ? { ...item, quantity: newQuantity }
          : item
      );

      setCart(updatedCart);
      localStorage.setItem("cart", JSON.stringify(updatedCart));
    },
    [cart]
  );

  const getTotalPrice = useCallback(() => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cart]);

  const handleCheckout = useCallback(() => {
    if (cart.length === 0) {
      alert("Your cart is empty. Please add items to proceed.");
      return;
    }
    navigate("/Store/checkout");
  }, [cart, navigate]);

  // Debug: Log image URLs to console
  useEffect(() => {
    if (cart.length > 0) {
      console.log("Cart items with image URLs:");
      cart.forEach((item, index) => {
        console.log(`Item ${index + 1}:`, {
          name: item.product_name,
          originalImage: item.product_image,
          processedUrl: getImageUrl(item.product_image)
        });
      });
    }
  }, [cart]);

  return (
    <div className="h-screen bg-gray-100 pt-20">
      <h1 className="mb-10 text-center text-2xl font-bold">Cart Items</h1>
      <div className="mx-auto max-w-5xl justify-center px-6 md:flex md:space-x-6 xl:px-0">
        {/* Cart Items */}
        <div className="rounded-lg md:w-2/3">
          {cart.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-lg">Your cart is empty</p>
              <button
                onClick={() => navigate("/Store")}
                className="mt-4 bg-black text-white px-6 py-2 rounded-md hover:bg-gray-700"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            cart.map((item) => (
              <CartItem
                key={item.product_id}
                item={item}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemoveFromCart}
              />
            ))
          )}
        </div>

        {/* Subtotal - Only show if cart has items */}
        {cart.length > 0 && (
          <div className="mt-6 h-full rounded-lg border bg-white p-6 shadow-md md:mt-0 md:w-1/3 space-y-2">
            <div className="border border-b-black">
              <p className="text-[22px] font-bold text-center">Cart Total</p>
            </div>
            <div className="mb-2 flex justify-between">
              <p className="text-gray-700">Subtotal</p>
              <p className="text-gray-700">₹{getTotalPrice().toFixed(2)}</p>
            </div>

            <hr className="my-4" />
            <div className="flex justify-between">
              <p className="text-lg font-bold">Total</p>
              <div>
                <p className="mb-1 text-lg font-bold">
                  ₹{getTotalPrice().toFixed(2)}
                </p>
              </div>
            </div>
            <button
              className="mt-6 w-full rounded-md bg-black py-1.5 font-medium text-blue-50 hover:bg-gray-700"
              onClick={handleCheckout}
            >
              Check out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;